import {
	Design,
	useZakekeHelpers,
	useZakekeProduct,
	useZakekeProductOptions,
	useZakekeTranslations,
	useZakekeUsedColors,
} from '@zakeke/zakeke-customizer-react';
import { Map } from 'immutable';

import { CustomizerElement, isImageElement, isTextArtElement, isTextElement } from '../components/interfaces';
import useSelectedSide from '../hooks/useSelectedSide';
import { isMobile } from '../shared/helpers';
import { checkImageResizable } from '../shared/helpers.image';
import { getDefaultTextColor } from '../shared/helpers.text';
import { SideAreaBackgrounds, useAppStore, useSetItems } from '../state/store';
import { isItemStatic, mapLibraryItemsToUIItems } from './common';
import useGetSelectedVariant from './useGetSelectedVariant';
import usePrintMethods from './usePrintMethods';
import useUndoRedo from './useUndoRedo';
import useVariantChanger from './useVariantChanger';
import useWaitUntilItemRerenderStops from './useWaitUntilItemRerenderStops';

const useAdaptTemplate = () => {
	const { getAdaptItems, getImage } = useZakekeHelpers();
	const setItems = useSetItems();
	const modelOptions = useZakekeProductOptions();
	const side = useSelectedSide();

	return async (currentTemplate: Design, currentVariantId: number, isFromTemplate: boolean) => {
		const variantOptions = modelOptions.find((x) => x.variantId === currentVariantId);
		const sideOptions = variantOptions?.sides?.find((x) => x.sideId === side!.id);
		const isResizable = sideOptions?.options?.isResizable ?? variantOptions?.options?.isResizable ?? false;

		if (!currentTemplate || !currentVariantId) return;
		if (!currentTemplate.variantId) return;
		if (currentTemplate.variantId === currentVariantId && !isResizable) return;
		if (!currentTemplate.adaptItemsToVariants) return;
		const templateVariantId = currentTemplate.variantId;
		const { texts, images, shapes, textArts } = currentTemplate;
		const guidAreaMapping = [texts, images, shapes, textArts].reduce((map, itemList) => {
			let newMap = map;
			itemList.forEach((item) => {
				const areasIds: { areaID: number; sideID: number }[] = [];
				item.areas?.forEach((areaID) => {
					areasIds.push({
						areaID,
						sideID: item.sideId,
					});
				});

				newMap = newMap.set(item.guid, areasIds);
			});
			return newMap;
		}, Map<string, { areaID: number; sideID: number }[]>());

		const newItems = await getAdaptItems(
			currentVariantId,
			templateVariantId,
			currentTemplate.adaptItemsToVariants,
			new window.Map(guidAreaMapping.entries()),
			currentTemplate.variantId === currentVariantId && isResizable
		);
		if (newItems) {
			const items = await mapLibraryItemsToUIItems(newItems, getImage, (i) => {
				i.isFromTemplate = isFromTemplate;
				return Promise.resolve(i);
			});
			if (items && items.length > 0) {
				setItems(() => items);
			}
		}
	};
};

const useDesignLoader = () => {
	const { adaptItemToPrintMethods, adaptSideToPrintMethod, handleAdaptItemToPrintMethodsErrors } = usePrintMethods();
	const { getDesign, getImage, getSelectableColorsForText } = useZakekeHelpers();
	const product = useZakekeProduct();
	// const currentVariant = useSelectedVariant();
	const getCurrentVariant = useGetSelectedVariant();
	const changeVariant = useVariantChanger();
	const { T } = useZakekeTranslations();
	const side = useSelectedSide();
	const { resetUndoRedo } = useUndoRedo();
	const usedColors = useZakekeUsedColors();
	const adaptTemplate = useAdaptTemplate();
	const waitUntilItemsRerenderStops = useWaitUntilItemRerenderStops();

	const [
		setSelectedItemGuid,
		openEditMenuPageForItem,
		setMenuPage,
		setSidesCollages,
		setEditDesignId,
		setSidesPrintTypes,
		setSidesRotations,
		setPreventAdaptForSideRotation,
		removeSideAreaBackgrounds,
		setCurrentDesign,
	] = useAppStore((x) => [
		x.setSelectedItemGuid,
		x.openEditMenuPageForItem,
		x.setMenuPage,
		x.setSidesCollages,
		x.setEditDesignId,
		x.setSidesPrintMethods,
		x.setSidesRotations,
		x.setPreventAdaptForSideRotation,
		x.removeSideAreaBackgrounds,
		x.setCurrentDesign,
	]);

	const setItems = useSetItems();

	/**
	 * Apply printing method to items, decorate and set up items and backgrounds
	 * @param design The design object to apply
	 * @return SideAreaBackgrounds The new sideAreaBackgrounds map
	 * test: remove background
	 * test: add new background
	 * test: adapt to print method
	 */
	const getBackgroundColorsFromDesign = (design: Design): SideAreaBackgrounds => {
		return design.backgroundColors.reduce((sideAreaBackgrounds: SideAreaBackgrounds, backgroundColor) => {
			return sideAreaBackgrounds.set(
				backgroundColor.sideId,
				(sideAreaBackgrounds.get(backgroundColor.sideId) || Map()).set(backgroundColor.areaId, {
					fillColor: backgroundColor.fillColor,
					imageItemGuid: null,
				})
			);
		}, Map() as SideAreaBackgrounds);
	};
	/**
	 * Apply printing method to items, decorate and set up items and backgrounds
	 * @param design The design object to apply
	 * @param type set if the design is a draft, template or design
	 * @param mapper trasformation function before setting items
	 * test: remove collage from all sides if exists
	 * test: adapt to print method
	 */
	const getItemsFromDesign = async (
		design: Design,
		type: 'template' | 'design',
		// type: 'draft' | 'template' | 'design' = 'draft', // al momento le draft sono gestite come i design, come avviene nel vecchio, la gestione di draft crea errori, va ricontrollata.
		mapper?: (item: CustomizerElement) => CustomizerElement
	): Promise<CustomizerElement[]> => {
		if (!product) return [];
		const currentVariant = getCurrentVariant();
		const isTemplate = type === 'template';
		// const isDraft = type === 'draft';
		const variantId = currentVariant?.id;
		// should set item to empty array before await, so can do rendering
		setItems(() => [], true);
		const errors: Error[] = [];
		const items = await mapLibraryItemsToUIItems(design, getImage, (originalItem) => {
			const item = {
				...originalItem,
				isFromTemplate: isTemplate,
			};

			const getPrintTypeId = () => {
				// let printTypeId = isDraft
				// 	? // if is a draft adapt print method to current side's print method
				// 	  getPrintMethodForSide(item.sideId)?.id
				// 	: // if is a design get his own print method
				// 	  design.sidePrintTypes.get(item.sideId)!;
				// if is a design get his own print method

				const _printTypeId = design.sidePrintTypes.get(item.sideId)!;
				const printTypeId =
					currentVariant?.sides
						?.find((x) => x.id === item.sideId)
						?.printTypes.find((x) => x.id === _printTypeId)?.id ?? null;
				// if there is no print type, get the default
				if (!printTypeId || printTypeId === -1) {
					const printType = product.variants
						.find((x) => x.id === variantId)
						?.sides.find((x) => x.id === item.sideId)?.printTypes[0];

					return printType?.id ?? product.printTypes[0].id!;
				}
				return printTypeId;
			};
			const printTypeId = getPrintTypeId();
			// some old templates have wrong printTypeID that dosn't exist anymore
			// the first printType is used if no one available
			const currentPrintType =
				product?.printTypes.find((x) => x.id === printTypeId) ?? product?.printTypes.at(0)!;

			// setup constraints
			if (isImageElement(item) && !item.isBackgroundForArea) {
				const isClipartsImagesResizeEnabled = currentPrintType.isClipartsImagesResizeEnabled;
				item.constraints = {
					...(item.constraints || {}),
					canResize: checkImageResizable(item, !!isClipartsImagesResizeEnabled),
				};
			}

			// set default text stroke color if undefined.
			// memo: this is a temporary solution,
			// We need to test each case with strokeColor === undefined to safely remove the next if block
			if (isTextElement(item) && item.strokeColor === undefined) {
				item.strokeColor = getDefaultTextColor(
					currentPrintType,
					getSelectableColorsForText(item.sideId, '#00000', printTypeId, false),
					usedColors?.get(item.sideId) ?? null
				);
			}

			if (
				isTextElement(item) &&
				item.fontSize &&
				item.constraints?.minFontSize &&
				item.fontSize < item.constraints.minFontSize
			) {
				item.fontSize = item.constraints.minFontSize;
			}

			if (
				isTextElement(item) &&
				item.fontSize &&
				item.constraints?.maxFontSize &&
				item.fontSize > item.constraints.maxFontSize
			) {
				item.fontSize = item.constraints.maxFontSize;
			}

			try {
				return adaptItemToPrintMethods(item, printTypeId, side?.id!, false);
			} catch (error) {
				if (error instanceof Error) errors.push(error);
				return Promise.resolve(null);
			}
		});

		if (errors) {
			const notRecolorableError = errors.find(
				(e) => e.message === 'Image contains more than 100 colors, rasters or gradients.'
			);
			handleAdaptItemToPrintMethodsErrors(notRecolorableError ?? errors[0]);
		}

		return mapper ? items.map(mapper) : items;
	};

	/**
	 * Apply the @design clearing all the other elements
	 * @param design The design object to apply
	 * @param isTemplate If the design is a template, if not then the variant will also change
	 */
	const applyDesign = async (design: Design, isTemplate: boolean) => {
		if (!product) return;
		const currentVariant = getCurrentVariant();
		// Get correct variant
		let variantId = currentVariant?.id;
		let variant = currentVariant;

		if (!isTemplate && design.variantId) {
			variantId = design.variantId;
		}

		// Select the first side
		if (variantId) variant = product.variants.find((x) => x.id === variantId);

		if (!variant) throw new Error(`Variant ${variantId} not found when applying design.`);

		setEditDesignId(design.id);
		await changeVariant(variant.id, {
			preventResetEnabled: true,
			preventAdapt: true,
			preventApplyPrintMethods: true,
		});

		/*
		 * In case of template created before assigning any print method to the product,
		 * design.sidePrintTypes is an empty map that creates only problems,
		 * so we prevent overwriting default assigned printTypes
		 *
		 * In case of template made on variant that have no common print method with current variant,
		 * we get the first valid print method for current variant
		 */
		if (design.sidePrintTypes.size > 0) {
			setSidesPrintTypes(
				Array.from(design.sidePrintTypes.entries()).reduce((newMap, [sideId, printTypeId]) => {
					const variantSide = currentVariant?.sides?.find((x) => x.id === sideId);
					const variantPrintType = variantSide?.printTypes.find((x) => x.id === printTypeId);
					if (!variantSide || !variantSide.printTypes[0]) {
						return newMap;
					}
					if (!variantPrintType) {
						printTypeId = variantSide.printTypes[0].id;
					}
					return newMap.set(sideId, printTypeId);
				}, Map<number, number>())
			);
		}

		// Rotation
		setSidesRotations(Map(design.sideRotations));
		setPreventAdaptForSideRotation(true);
		removeSideAreaBackgrounds();
		setItems(() => [], true);

		setSidesCollages(Map(design.sidesCollages));
		const translateTextualItems = (x: CustomizerElement) => {
			if (isTextArtElement(x) || isTextElement(x))
				return { ...x, content: T._d(x.content), originalContent: T._d(x.content) };
			return x;
		};

		const sideAreaBackgrounds = getBackgroundColorsFromDesign(design);
		const unadaptedItems = await getItemsFromDesign(
			design,
			isTemplate ? 'template' : 'design',
			translateTextualItems
		);
		// fallback in caso il printMethod del template non esista più.
		const adapted = await [...useAppStore.getState().sidesPrintMethods.entries()].reduce(
			async (accPromise, [sideId, printTypeId]) => {
				if (printTypeId && product.printTypes.some((x) => x.id === printTypeId)) {
					return accPromise;
				}
				const newPrintTypeId = product.printTypes[0].id;
				const acc = await accPromise;
				return adaptSideToPrintMethod(
					product,
					acc.sidePrintMethods,
					acc.sideAreaBackgrounds,
					variant!.id,
					acc.items,
					sideId,
					newPrintTypeId
				);
			},
			Promise.resolve({
				items: unadaptedItems,
				sideAreaBackgrounds,
				sidePrintMethods: useAppStore.getState().sidesPrintMethods,
			})
		);

		// Filter all elements on sides that don't exist on teh current variant
		const items = adapted.items.filter((item) => {
			const side = variant?.sides.find((x) => x.id === item.sideId);
			if (!side) return false;

			return true;
		});

		useAppStore.setState({
			sidesPrintMethods: adapted.sidePrintMethods,
			sideAreaBackgrounds: items.reduce((sideAreaBackgrounds, item) => {
				if (!isImageElement(item)) {
					return sideAreaBackgrounds;
				}
				if (!item.isBackgroundForArea) {
					return sideAreaBackgrounds;
				}
				return sideAreaBackgrounds.set(
					item.sideId,
					(sideAreaBackgrounds.get(item.sideId) || Map()).set(item.isBackgroundForArea, {
						fillColor: null,
						imageItemGuid: item.guid,
					})
				);
			}, adapted.sideAreaBackgrounds),
		});
		setItems(() => items, true);

		await waitUntilItemsRerenderStops(items.map((x) => x.guid));

		const firstSideId = variant!.sides[0].id;

		await adaptTemplate(design, variant.id, isTemplate);
		// Select the first element of the first side if only one and is somehow editable
		const firstSideItems = items.filter((x) => {
			// const isItemTopOrBottom =
			// 	(x.constraints?.isAlwaysOnTop && x.constraints.isAlwaysOnTop === true) ||
			// 	(x.constraints?.isAlwaysOnBottom && x.constraints.isAlwaysOnBottom === true);
			return x.sideId === firstSideId && !isItemStatic(x);
		});

		// If there is a background color for the first side, we don't select the side single item
		const backgroundColorsForSide =
			sideAreaBackgrounds.get(firstSideId) && sideAreaBackgrounds.get(firstSideId)!.size > 0;

		if (firstSideItems.length === 1 && !backgroundColorsForSide) {
			const itemToSelect = firstSideItems[0];

			if (itemToSelect && !isItemStatic(itemToSelect)) {
				setSelectedItemGuid(itemToSelect?.guid);

				if (!isMobile()) {
					openEditMenuPageForItem(itemToSelect, false);
				}
			} else {
				setSelectedItemGuid(null);
			}
		}

		resetUndoRedo();

		// If multiple elements, open design elements
		if (!isMobile() && (firstSideItems.length > 1 || (firstSideItems.length === 1 && backgroundColorsForSide))) {
			setMenuPage({ page: 'design-elements' });
		}
	};

	/**
	 * Load a existing design (e.g edit design or draft)
	 * @param id The design to load
	 */
	const loadDesign = async (id: string, isOrderDesignEditor: boolean) => {
		const variantId = getCurrentVariant()?.id;
		const design = await getDesign(id, isOrderDesignEditor, variantId);
		applyDesign(design, false);
		setCurrentDesign(design);
	};

	return {
		applyDesign,
		loadDesign,
	};
};

export default useDesignLoader;
